/* eslint-disable camelcase */
import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios';
import dayjs from 'dayjs';
import VuetifyConfirm from 'vuetify-confirm';
import auroraModule from '@codehq/aurora-app-core/src/plugins/auroraModule';
import token from '@codehq/aurora-app-core/src/mixins/token';
import { authHub, errorHub, components } from '@codehq/aurora-app-core/src';
import LocaleSwitcher from '@codehq/aurora-app-core/src/components/LocaleSwitcher.vue';
import TableBasicButtons from '@codehq/aurora-app-core/src/components/TableBasicButtons.vue';
import * as VueGoogleMaps from 'vue2-google-maps';
import VCalendar from 'v-calendar';
import { o } from 'odata';
import { mapActions, mapGetters, mapState } from 'vuex';

import App from './App.vue';
import odataPlugin from './plugins/odataPlugin';
import vuetify from './plugins/vuetify';
import store from './store';
import { router, routes } from './router';
import { i18n, messages } from './i18n';
import appColours from './appColours';
import native from './native';

// PWA Service worker
if (process.env.NODE_ENV === 'production') {
  // eslint-disable-next-line global-require
  const wb = require('./registerServiceWorker');

  Vue.prototype.$workbox = wb;
}
Vue.config.productionTip = false;

Vue.use(VueAxios, axios);

Vue.use(VuetifyConfirm, { vuetify });

Vue.use(VCalendar, {
  componentPrefix: 'vc',
});

const { ImageUpload } = components;
// register aurora components and plugin
Vue.component(LocaleSwitcher.name, LocaleSwitcher);
Vue.component(TableBasicButtons.name, TableBasicButtons);
Vue.component(ImageUpload.name, ImageUpload);
Vue.use(auroraModule, {
  baseUrl: process.env.VUE_APP_APIURL,
  store,
});
Vue.use(odataPlugin, store);
store.$o = {
  version: '0.0.1',
  store,
  client() {
    const authToken = this.store.getters['auth/getToken'];
    const organization = this.store.getters['organizations/getOrganization'];
    return o(`${process.env.VUE_APP_APIURL}/odata/`, {
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        organizationid: `${organization?.id}`,
      }),
      onError: (x, e) => {
        if (e.status === 401) {
          authHub.$emit('user.unauthorized', e);
        } else {
          errorHub.$emit('network.error', e);
        }
      },
    });
  },
  parseId: (id) => {
    let i = id;
    if (Number.isNaN(id)) {
      i = `'${id}'`;
    }
    return i;
  },
};

store.$http = axios.create({
  baseURL: process.env.VUE_APP_APIURL,
});

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_API_KEY,
    libraries: 'places,drawing,visualization',
  },
});

// format
Vue.mixin({
  methods: {
    formatDate(dt) {
      return dayjs(dt).format('YYYY-MM-DD');
    },
    formatDateTime(dt) {
      return dayjs(dt).format('YYYY-MM-DD HH:mm');
    },
    formatFromNow(dt) {
      return dayjs(dt).fromNow();
    },
  },
});

// Setup API
axios.defaults.baseURL = process.env.VUE_APP_APIURL;

o(process.env.VUE_APP_APIURL);

new Vue({
  vuetify,
  store,
  router,
  render: (h) => h(App),

  computed: {
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapState('auth', ['token']),
    ...mapState('organizations', ['organization', 'organizations']),
  },

  i18n,

  mixins: [appColours, native, token],
  async created() {
    this.$log.info('init auth');
    await this.$store.dispatch('auth/INIT_auth', {
      url: process.env.VUE_APP_APIURL,
      client_id: process.env.VUE_APP_CLIENT_ID,
      client_secret: process.env.VUE_APP_CLIENT_SECRET,
    });
    // eslint-disable-next-line no-unused-vars
    navigator.serviceWorker.addEventListener('message', (event) => {
      // console.log(event.data.message);
      this.REFRESH_auth();
    });
  },
  mounted() {
    this.$log.debug('Locales:');
    this.$log.debug(messages);
    this.$log.debug('Routes:');
    this.$log.debug(routes);
  },
  destroyed() {
    this.$root.$off('auth:login');
  },
  methods: {
    ...mapActions('auth', ['INIT_auth', 'REFRESH_auth']),
  },
}).$mount('#app');
